import { Controller } from "@hotwired/stimulus";
import * as Luxon from "luxon";

/**
 * The purpose of this controller is to be attached to a form with date and/or time controls.
 * It can listen for changes on these form items and edit a hidden form item with an iso8601
 * compliant date and/or time string. It takes in the form names of various form items and
 * a timezone string. It will then update the iso8601 form item with the latest iso8601 string
 * based on a timezone.
 *
 * TODO: This should probably use targets instead of ids or names.
 * 
 *  * Supported modes (in order):
 * 1) Targets (recommended for repeatable rows):
 *      data-form-iso8601-transformation-target="date|time|iso"
 * 2) Legacy IDs (sitewide today):
 *      data-use-ids="true" + data-date-name="some_id" etc.
 * 3) Name lookup inside this.element:
 *      data-date-name="some[name]" etc.
 */

export default class extends Controller {
  static targets = ["date", "time", "iso"];

  connect() {
    // console.log("Connected iso time controller");
    setTimeout(() => {
      this.attemptToAddEventListeners();
      this.updateTimeStrings();
      this.populateIso8601Element();
    });
  }

  latestTimeString = "";
  latestDateString = "";
  latestIso8601String = "";

  hasAddedDateListener = false;
  hasAddedTimeListener = false;

  findDateElement() {
    // Prefer targets (best for repeated rows)
    if (this.hasDateTarget) return this.dateTarget;

    const dateName = this.data.get("dateName");
    if (!dateName) return null;

    const useIds = !!this.data.get("useIds");

    return useIds
      ? document.getElementById(dateName)
      : this.element.querySelector('input[name="' + dateName + '"]');
  }

  findTimeElement() {
    if (this.hasTimeTarget) return this.timeTarget;

    const timeName = this.data.get("timeName");
    if (!timeName) return null;

    const useIds = !!this.data.get("useIds");

    return useIds
      ? document.getElementById(timeName)
      : this.element.querySelector('input[name="' + timeName + '"]');
  }

  findIsoElement() {
    if (this.hasIsoTarget) return this.isoTarget;

    const isoName = this.data.get("isoName");
    if (!isoName) return null;

    const useIds = !!this.data.get("useIds");

    return useIds
      ? document.getElementById(isoName)
      : this.element.querySelector('input[name="' + isoName + '"]');
  }

  attemptToAddEventListeners() {
    const timeElement = this.findTimeElement();

    if (timeElement && !this.hasAddedTimeListener) {
      timeElement.addEventListener("change", (event) => {
        this.updateTimeStrings();
        this.populateIso8601Element();
        this.attemptToAddEventListeners();
      });
      this.hasAddedTimeListener = true;
    }

    const dateElement = this.findDateElement();
    if (dateElement && !this.hasAddedDateListener) {
      dateElement.addEventListener("change", (event) => {
        this.updateTimeStrings();
        this.populateIso8601Element();
        this.attemptToAddEventListeners();
      });
      this.hasAddedDateListener = true;
    }
  }

  updateTimeStrings() {
    const timeElement = this.findTimeElement();
    const dateElement = this.findDateElement();

    this.latestTimeString = timeElement ? timeElement.value : "";
    this.latestDateString = dateElement ? dateElement.value : "";

    const timezone = this.data.get("timezone");
    const dateTime =
      this.latestTimeString && this.latestDateString
        ? Luxon.DateTime.fromISO(
            this.latestDateString + "T" + this.latestTimeString,
            { zone: timezone }
          )
        : undefined;

    this.latestIso8601String = dateTime ? dateTime.toString() : "";
  }

  populateIso8601Element() {
    const isoElement = this.findIsoElement();
    if (isoElement) {
      isoElement.value = this.latestIso8601String;
      isoElement.dispatchEvent(
        new Event("change", {
          bubbles: true,
          cancelable: true,
        })
      );
    }
  }
}

function optionalChaining(valueFn) {
  try {
    return valueFn();
  } catch (e) {
    return undefined;
  }
}
